/**
 * Weather Value Sanitizer
 * Converts invalid sentinel values to null for safe display
 */

// Sentinel values used by rainowls-shared
const INVALID_INT = Number.MIN_SAFE_INTEGER; // -9007199254740991
const INVALID_DOUBLE = -999.99;
const INVALID_INT_LEGACY = -999;

/**
 * Check if a numeric value is a known invalid/sentinel value
 */
export function isInvalidValue(value: number | null | undefined): boolean {
  if (value === null || value === undefined) return true;
  if (!Number.isFinite(value)) return true;
  if (value === INVALID_INT) return true;
  if (value === INVALID_DOUBLE) return true;
  if (value === INVALID_INT_LEGACY) return true;
  return false;
}

/**
 * Sanitize a weather value - returns null if invalid, otherwise the value
 */
export function sanitizeValue(value: number | null | undefined): number | null {
  if (isInvalidValue(value)) return null;
  return value as number;
}

/**
 * Sanitize a weather value with a default fallback
 */
export function sanitizeValueWithDefault(
  value: number | null | undefined,
  defaultValue: number
): number {
  const sanitized = sanitizeValue(value);
  return sanitized !== null ? sanitized : defaultValue;
}

/**
 * Sanitize wind speed - returns null for invalid or negative values
 * Wind speed is always non-negative; negative values indicate bad/sentinel data
 */
export function sanitizeWindSpeed(value: number | null | undefined): number | null {
  const sanitized = sanitizeValue(value);
  if (sanitized === null || sanitized < 0) return null;
  return sanitized;
}

/**
 * Format wind speed for display - returns "--" for invalid values
 * Wind speed is always non-negative; negative values indicate bad/sentinel data
 */
export function formatWindSpeed(value: number | null | undefined): string {
  const sanitized = sanitizeWindSpeed(value);
  if (sanitized === null) return "--";
  return Math.round(sanitized).toString();
}

/**
 * Format temperature for display - returns "--" for invalid values
 */
export function formatTemperature(value: number | null | undefined): string {
  const sanitized = sanitizeValue(value);
  if (sanitized === null) return "--";
  return Math.round(sanitized).toString();
}

/**
 * Format humidity for display - returns "--" for invalid values
 */
export function formatHumidity(value: number | null | undefined): string {
  const sanitized = sanitizeValue(value);
  if (sanitized === null) return "--";
  return Math.round(sanitized).toString();
}

/**
 * Format pressure for display - returns "--" for invalid values
 */
export function formatPressure(value: number | null | undefined): string {
  const sanitized = sanitizeValue(value);
  if (sanitized === null) return "--";
  return sanitized.toFixed(1);
}
